/**
 * Prod-auth token provider (real login against the prod IdP).
 *
 * Oxbo-M (`m.oxbo.energy`) requires `Authorization: Bearer <JWT>` on every data
 * call. Unlike the dev-auth provider — which mints a token from the unguarded
 * `_dev/mint` endpoint — this module performs a REAL login against the prod
 * identity provider (`auth.qa.oxbo.energy`) using a demo user's email +
 * password, holds the access token in memory for the mApi baseQuery to read,
 * and keeps it fresh:
 *
 *   1. On start it POSTs `${VITE_OXBO_AUTH_URL}/v1/auth/login` with the demo
 *      credentials and stores the returned access + refresh tokens.
 *   2. A short margin before the access token's `exp` it rotates via
 *      `${VITE_OXBO_AUTH_URL}/v1/auth/refresh` (rotating refresh token).
 *   3. If refresh fails (revoked/expired refresh token), it falls back to a
 *      full re-login with the demo credentials, so the app never gets stuck
 *      token-less.
 *
 * It is gated entirely behind `VITE_PROD_AUTH=true`. When unset/false none of
 * this runs (the dev-auth provider and/or MSW cover the other build modes).
 *
 * Oxbo-P (pApi) needs no token — `OXBO_P_AUTH_REQUIRED=false` — so it never
 * consults this module.
 *
 * Demo credentials come from the build env (`VITE_DEMO_EMAIL` /
 * `VITE_DEMO_PASSWORD`); they are NOT hard-coded here.
 */

/** Refresh this many ms before the token's expiry so a call never races exp. */
const REFRESH_LEAD_MS = 2 * 60 * 1000; // 2 min
/** Fallback access-token lifetime if the JWT carries no readable `exp`. */
const FALLBACK_LIFETIME_MS = 15 * 60 * 1000;
/** Assume at least this much runway so a bad clock can't schedule exp in the past. */
const MIN_LIFETIME_MS = 60 * 1000;
/** Back-off before retrying a failed login. */
const RETRY_DELAY_MS = 5 * 1000;

/** Whether prod-auth is active for this build. */
export function isProdAuthEnabled(): boolean {
  return import.meta.env.VITE_PROD_AUTH === "true";
}

function authBase(): string {
  return import.meta.env.VITE_OXBO_AUTH_URL ?? "/api/auth";
}

function loginUrl(): string {
  return `${authBase()}/v1/auth/login`;
}

function refreshUrl(): string {
  return `${authBase()}/v1/auth/refresh`;
}

function demoEmail(): string {
  return import.meta.env.VITE_DEMO_EMAIL ?? "";
}

function demoPassword(): string {
  return import.meta.env.VITE_DEMO_PASSWORD ?? "";
}

let accessToken: string | null = null;
let refreshToken: string | null = null;
let refreshTimer: ReturnType<typeof setTimeout> | null = null;
let inFlight: Promise<string | null> | null = null;

/** The current prod access token, or null if none has been obtained yet. */
export function getProdToken(): string | null {
  return accessToken;
}

/** Decode an arbitrary claim map from a JWT payload, or null if unreadable. */
function decodeClaims(token: string): Record<string, unknown> | null {
  try {
    const [, payload] = token.split(".");
    if (!payload) return null;
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/** The `org_roles` claim of the current access token (empty if none). */
export function getProdOrgRoles(): string[] {
  if (!accessToken) return [];
  const claims = decodeClaims(accessToken);
  const roles = claims?.org_roles;
  return Array.isArray(roles) ? roles.filter((r): r is string => typeof r === "string") : [];
}

/** Decode a JWT's `exp` (seconds) → absolute ms, or null if unreadable. */
function readExpMs(token: string): number | null {
  const claims = decodeClaims(token);
  const exp = claims?.exp;
  return typeof exp === "number" ? exp * 1000 : null;
}

interface TokenPair {
  access_token?: string;
  refresh_token?: string;
}

/** Full login with the demo credentials. Throws on any non-ok response. */
async function loginOnce(): Promise<TokenPair> {
  const email = demoEmail();
  const password = demoPassword();
  if (!email || !password) {
    throw new Error("prod-auth: VITE_DEMO_EMAIL / VITE_DEMO_PASSWORD not set");
  }
  const res = await fetch(loginUrl(), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });
  if (!res.ok) {
    throw new Error(`prod-auth login failed: ${res.status} ${res.statusText}`);
  }
  const body = (await res.json()) as TokenPair;
  if (!body.access_token) {
    throw new Error("prod-auth login returned no access_token");
  }
  return body;
}

/** Rotate the refresh token → new pair. Throws if no refresh token or non-ok. */
async function refreshOnce(): Promise<TokenPair> {
  if (!refreshToken) throw new Error("prod-auth: no refresh token to rotate");
  const res = await fetch(refreshUrl(), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refresh_token: refreshToken }),
  });
  if (!res.ok) {
    throw new Error(`prod-auth refresh failed: ${res.status} ${res.statusText}`);
  }
  const body = (await res.json()) as TokenPair;
  if (!body.access_token) {
    throw new Error("prod-auth refresh returned no access_token");
  }
  return body;
}

function storePair(pair: TokenPair): string {
  accessToken = pair.access_token ?? null;
  if (pair.refresh_token) refreshToken = pair.refresh_token;
  if (accessToken) scheduleRefresh(accessToken);
  return accessToken ?? "";
}

/** Schedule the next refresh a safe margin before the access token expires. */
function scheduleRefresh(token: string): void {
  if (refreshTimer) clearTimeout(refreshTimer);
  const expMs = readExpMs(token);
  const lifetime = expMs ? expMs - Date.now() : FALLBACK_LIFETIME_MS;
  const delay = Math.max(MIN_LIFETIME_MS, lifetime - REFRESH_LEAD_MS);
  refreshTimer = setTimeout(() => {
    void renewProdToken();
  }, delay);
}

/**
 * Renew the access token: try a refresh-token rotation first; on any failure
 * fall back to a full re-login with the demo credentials. Retries on total
 * failure with a short back-off, so a transient auth-service blip never leaves
 * the app permanently token-less. Concurrent callers share one in-flight renew.
 */
export function renewProdToken(): Promise<string | null> {
  if (inFlight) return inFlight;
  inFlight = (async () => {
    try {
      let pair: TokenPair;
      if (refreshToken) {
        try {
          pair = await refreshOnce();
        } catch (refreshErr) {
          if (typeof console !== "undefined") {
            console.warn("[prodAuth] refresh failed, re-logging in", refreshErr);
          }
          pair = await loginOnce();
        }
      } else {
        pair = await loginOnce();
      }
      return storePair(pair);
    } catch (err) {
      // Keep any previously-valid token; retry shortly.
      if (typeof console !== "undefined") {
        console.warn("[prodAuth] renew failed, retrying", err);
      }
      if (refreshTimer) clearTimeout(refreshTimer);
      refreshTimer = setTimeout(() => {
        void renewProdToken();
      }, RETRY_DELAY_MS);
      return accessToken;
    } finally {
      inFlight = null;
    }
  })();
  return inFlight;
}

/**
 * Initialize prod auth at app start: log the demo user in (so the initial
 * render already has a token) and arm the refresh loop. No-op unless prod-auth
 * is enabled. Never throws — a failed initial login schedules a retry.
 */
export async function initProdAuth(): Promise<void> {
  if (!isProdAuthEnabled()) return;
  await renewProdToken();
}

/** Test-only: clear all in-memory state and timers. */
export function __resetProdAuthForTests(): void {
  accessToken = null;
  refreshToken = null;
  inFlight = null;
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
}
